import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client/react';
import cn from 'clsx';
import style from './style.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { TextAreaController } from '@components-clean/core/text-area';
import { ExtendedRiskObservationFields } from '@pages-clean/risk-assessment/risk-log/constants';
import { useTranslation } from '@hooks/index';
import { ShowError } from '@utils/notification';
import { GET_RISK_OBSERVATIONS_AND_ACTIONS, RESTORE_RISK_OBSERVATION } from '@queries/risk-assessment';

export interface ObservationDeleteDialogProps {
    onClose: () => void;
    onSubmit: () => void;
    observation: ExtendedRiskObservationFields;
    riskId: string;
    studyId: string;
}

export type FormInputs = {
    restoreReason: string | null;
};

const RestoreObservationDialog = ({
    onClose,
    onSubmit,
    observation,
    riskId,
    studyId,
}: ObservationDeleteDialogProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.restoreObservation' });
    const { t: tCommon } = useTranslation('common');
    const [restoreRiskObsMutation] = useMutation(RESTORE_RISK_OBSERVATION);
    const actionsCount = observation.actions?.length ?? 0;
    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            restoreReason: null,
        },
    });

    const restore = async (data: FormInputs, unDeleteActions: boolean) => {
        restoreRiskObsMutation({
            variables: {
                id: observation.id,
                unDeleteActions,
                reason: data.restoreReason,
            },
            refetchQueries: [
                {
                    query: GET_RISK_OBSERVATIONS_AND_ACTIONS,
                    variables: {
                        studyId,
                        riskId,
                    },
                },
            ],
        })
            .then(async (response) => {
                if (!response?.data?.unDeleteRiskObservation) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                onSubmit();
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const onRestore = async (data: FormInputs) => {
        await restore(data, false);
    };

    const onRestoreBoth = async (data: FormInputs) => {
        await restore(data, true);
    };

    const warningText =
        actionsCount > 0 ? (
            <span className={cn('k-text-left', style.fontSize)}>
                {t('warningMessage2', {
                    count: actionsCount,
                })}
            </span>
        ) : (
            <span className={cn('k-text-left', style.fontSize)}>{t('warningMessage')}</span>
        );

    return (
        <DraggableDialog
            dataTestId="delete-observation-warning"
            onSubmit={() => handleSubmit(onRestore)()}
            onClose={onClose}
            title={`${t('title')}: ${observation.recordId}`}
            initialWidth={actionsCount > 0 ? 600 : 530}
            initialHeight={280}
            height={null}
            primaryBtnLabel={actionsCount === 0 ? t('common:restore') : t('buttons.restoreObservation')}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            additionalBtnLabel={actionsCount > 0 ? t('buttons.restoreObservationAndActions') : undefined}
            additionalBtnDisabled={!isValid || !isDirty || isSubmitting}
            onAdditionalAction={actionsCount > 0 ? () => handleSubmit(onRestoreBoth)() : undefined}
        >
            <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                <InfoField type="Warning" message={warningText} />
                <TextAreaController
                    className={style.textAreaOverrides}
                    maxLength={3000}
                    control={control}
                    name="restoreReason"
                    required
                    data-testid="field-observation-restoreReason"
                    label={`${t('restoreReasonLabel')} *`}
                />
            </div>
        </DraggableDialog>
    );
};

export default RestoreObservationDialog;
